import { useTheme } from '../contexts/ThemeContext';

// Custom hook that provides theme-aware Tailwind classes
export const useThemeClasses = () => {
  // Defensive error handling - provide fallback if context is unavailable
  let isDark = false;
  try {
    const context = useTheme();
    isDark = context?.isDark || false;
  } catch (error) {
    // If ThemeProvider is not available, default to light mode
    console.warn('useThemeClasses: ThemeContext not available, using light mode defaults');
    isDark = false;
  }

  return {
    // Backgrounds
    bgPrimary: isDark ? 'bg-gray-900' : 'bg-white',
    bgSecondary: isDark ? 'bg-gray-800' : 'bg-gray-50',
    bgTertiary: isDark ? 'bg-gray-700' : 'bg-gray-100',
    bgHover: isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-50',
    
    // Text colors
    textPrimary: isDark ? 'text-white' : 'text-gray-900',
    textSecondary: isDark ? 'text-gray-300' : 'text-gray-600',
    textTertiary: isDark ? 'text-gray-400' : 'text-gray-500',
    textMuted: isDark ? 'text-gray-500' : 'text-gray-400',
    
    // Borders
    border: isDark ? 'border-gray-700' : 'border-gray-200',
    borderHover: isDark ? 'hover:border-gray-600' : 'hover:border-gray-300',
    borderFocus: isDark ? 'focus:border-blue-400' : 'focus:border-blue-500',
    
    // Inputs
    input: isDark 
      ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:ring-blue-500 focus:border-blue-500' 
      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-blue-500 focus:border-blue-500',
    
    // Cards
    card: isDark 
      ? 'bg-gray-800 border-gray-700' 
      : 'bg-white border-gray-200',
    cardHover: isDark 
      ? 'hover:bg-gray-750 hover:border-gray-600' 
      : 'hover:bg-gray-50 hover:border-gray-300',
    
    // Tables
    tableHeader: isDark ? 'bg-gray-800' : 'bg-gray-50',
    tableRow: isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-50',
    tableBorder: isDark ? 'divide-gray-700' : 'divide-gray-200',
    
    // Modals & overlays
    overlay: 'bg-black bg-opacity-50',
    modal: isDark 
      ? 'bg-gray-800 border-gray-700' 
      : 'bg-white border-gray-200',
    
    // Buttons (secondary/ghost)
    btnSecondary: isDark 
      ? 'bg-gray-700 text-white hover:bg-gray-600 border-gray-600' 
      : 'bg-gray-200 text-gray-700 hover:bg-gray-300 border-gray-300',
    
    // Dropdowns
    dropdown: isDark 
      ? 'bg-gray-800 border-gray-700' 
      : 'bg-white border-gray-200',
    dropdownItem: isDark 
      ? 'hover:bg-gray-700 text-gray-300' 
      : 'hover:bg-gray-100 text-gray-700',
    
    // Badges/Tags (adapt based on color)
    badgeGreen: isDark 
      ? 'bg-green-900 text-green-200' 
      : 'bg-green-100 text-green-800',
    badgeYellow: isDark 
      ? 'bg-yellow-900 text-yellow-200' 
      : 'bg-yellow-100 text-yellow-800',
    badgeRed: isDark 
      ? 'bg-red-900 text-red-200' 
      : 'bg-red-100 text-red-800',
    badgeBlue: isDark 
      ? 'bg-blue-900 text-blue-200' 
      : 'bg-blue-100 text-blue-800',
    badgeGray: isDark 
      ? 'bg-gray-700 text-gray-300' 
      : 'bg-gray-100 text-gray-600',
    
    // Alerts
    alertInfo: isDark 
      ? 'bg-blue-900 border-blue-700 text-blue-200' 
      : 'bg-blue-50 border-blue-200 text-blue-800',
    alertSuccess: isDark 
      ? 'bg-green-900 border-green-700 text-green-200' 
      : 'bg-green-50 border-green-200 text-green-800',
    alertWarning: isDark 
      ? 'bg-yellow-900 border-yellow-700 text-yellow-200' 
      : 'bg-yellow-50 border-yellow-200 text-yellow-800',
    alertError: isDark 
      ? 'bg-red-900 border-red-700 text-red-200' 
      : 'bg-red-50 border-red-200 text-red-800',
    
    // Helper values
    isDark
  };
};